import Logo from "@/components/Logo";
import GoogleAuthButton from "@/components/auth/GoogleAuthButton";
import { Button } from "@/components/ui/Button";
import Paragraph from "@/components/ui/Paragraph";
import { getGradientBg } from "@/components/util/getGradientBg";
import { cn } from "@/lib/utils";
import { isUsedDeviceIphone } from "@/utils/isUsedDeviceIphone";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import RegisterForm from "./components/RegisterForm";
import TextsWrapper from "./components/TextsWrapper";
import UseMobileAppToRegisterCard from "./components/UseMobileAppToRegisterCard";
import UseMobileToRegisterAlert from "./components/UseMobileToRegisterAlert";
import ContactUsPrompt from "@/components/ContactUsPrompt";

const Register = () => {
  const gradientBg = useMemo(() => getGradientBg(), []);
  const router = useRouter();
  const { referralLink } = router.query;
  const isRegisterDisabled = !isMobile && !referralLink;
  // register available just for IOS devices
  const isIphone = isUsedDeviceIphone();

  const { t } = useTranslation("register");

  return (
    <>
      <Head>
        <title>{t("new_account_registration")}</title>
      </Head>

      <main
        className={cn(
          "flex w-full min-h-svh relative text-foreground max-lg:p-4 gap-6",
          gradientBg,
          {
            "grid place-items-center": !isIphone,
          }
        )}
      >
        <Image
          className="absolute inset-0 object-cover animate-opacityIncrease"
          layout="fill"
          src="/images/logo/logo-pattern.png"
          alt="logo pattern background"
        />

        {!isIphone ? (
          <div className="z-10 flex flex-col gap-12 mt-4">
            <UseMobileAppToRegisterCard />
            <ContactUsPrompt />
          </div>
        ) : (
          <>
            <div className="flex flex-col items-center bg-background z-10 rounded-xl lg:rounded-e-[48px] lg:rounded-s-none max-w-full max-lg:h-fit transition-transform ltr:lg:animate-slideInFromLeft rtl:lg:animate-slideInFromRight py-12 2xl:py-24 p-6 max-lg:mx-auto lg:flex-1">
              <Link href="/" passHref>
                <a className="mx-auto block mb-8 2xl:mb-16">
                  <Logo />
                </a>
              </Link>

              <div className="w-[500px] max-w-full lg:mx-6 grid gap-6 md:gap-8">
                <div className="w-full flex justify-between items-center">
                  <Paragraph className="text-xl" variant="subTitle">
                    {t("create_account_button")}
                  </Paragraph>
                  <Link href="/login" passHref>
                    <Button
                      className={`px-0 before:content-['${t(
                        "or"
                      )}'] text-foreground hover:text-primary before:hover:text-foreground before:pointer-events-none !no-underline`}
                      variant="link"
                    >
                      {t("login_button")}
                    </Button>
                  </Link>
                </div>
                <GoogleAuthButton skipPhoneRegistrationPage={!referralLink} />
                <RegisterForm />
              </div>

              <div className="max-lg:hidden mt-6">
                <ContactUsPrompt hideShadow />
              </div>
            </div>

            <div className="lg:hidden">
              <ContactUsPrompt />
            </div>

            {isRegisterDisabled && <UseMobileToRegisterAlert />}

            <TextsWrapper />
          </>
        )}
      </main>
    </>
  );
};

Register.theme = "light";

export default Register;
